// src/services/notificationBusService.ts
// ─────────────────────────────────────────────────────────────────
// Notification Bus Service — Phase 5 (Mock)
//
// Phase 5 additions over Phase 1:
//   • resendNotification()           — re-dispatch to unacknowledged
//   • escalateToWHSOAlert()          — create DRAFT WHSO alert from notification
//   • getNotificationsGroupedByDate()— for Hub date-grouped view
//   • getDeliveryStatusSummary()     — per-notification ack breakdown
//   • exportNotificationsToCSV()     — WHS audit trail export
//   • searchNotifications()          — keyword search across heading+body
//
// At beta: replace function bodies with fetch('/api/notifications/crew/...')
// ─────────────────────────────────────────────────────────────────

import {
  addCrewNotification as _add,
  getCrewNotifications as _get,
  acknowledgeNotification as _ack,
  getUnreadCount as _count,
  getNotificationById as _byId,
  type AddNotificationInput,
  type GetNotificationsFilter,
} from '../utils/mockDataNotificationBus'
import type { CrewNotification, NotificationType } from '../types/mobileCrewTypes'

// ── Existing functions (Phase 1 — unchanged) ──────────────────────

export async function addCrewNotification(input: AddNotificationInput): Promise<CrewNotification> {
  return Promise.resolve(_add(input))
}

export async function getCrewNotifications(filter?: GetNotificationsFilter): Promise<CrewNotification[]> {
  return Promise.resolve(_get(filter))
}

export async function acknowledgeNotification(
  notificationId: string,
  employeeId:     string,
  employeeName:   string,
): Promise<CrewNotification | null> {
  return Promise.resolve(_ack(notificationId, employeeId, employeeName))
}

export async function getUnreadCount(
  employeeId?: string,
  types?:      NotificationType[],
): Promise<number> {
  return Promise.resolve(_count(employeeId, types))
}

export async function getNotificationById(id: string): Promise<CrewNotification | null> {
  return Promise.resolve(_byId(id))
}

// ── Phase 5 additions ─────────────────────────────────────────────

/** Re-dispatch a notification to any crew members who haven't acknowledged. */
export async function resendNotification(notificationId: string): Promise<number> {
  // Phase 6: POST /api/notifications/crew/:id/resend
  const n = _byId(notificationId)
  if (!n) return 0

  const unacknowledgedIds = n.employeeIds.filter(
    id => !n.acknowledgements.find(a => a.employeeId === id)
  )
  if (!unacknowledgedIds.length) return 0

  // In mock environment: create a new SENT status clone for the unacknowledged crew
  _add({
    type:          n.type,
    priority:      n.priority,
    employeeIds:   unacknowledgedIds,
    jobIds:        n.jobIds,
    heading:       `REMINDER: ${n.heading}`,
    body:          n.body,
    requiredAction:n.requiredAction,
    sourceModule:  n.sourceModule,
    sourceRecordId:n.sourceRecordId,
    requiresAck:   n.requiresAck,
    payload:       n.payload as Record<string, unknown>,
  })

  return unacknowledgedIds.length
}

/**
 * Escalate a notification by creating a DRAFT WHSO alert from it.
 * Useful when a standard push notification hasn't been acknowledged and
 * the PM wants to escalate to a formal WHS alert requiring on-screen ack.
 */
export async function escalateToWHSOAlert(
  notificationId: string,
  pmId:           string,
  pmName:         string,
): Promise<boolean> {
  // Phase 6: POST /api/notifications/crew/:id/escalate
  const n = _byId(notificationId)
  if (!n) return false

  // Lazy import to avoid circular dependency
  const { createAlert } = await import('./whsoAlertService')
  await createAlert({
    alertType:               'WHS_GENERAL',
    severity:                (n.priority === 'CRITICAL' || n.priority === 'HIGH') ? n.priority : 'MEDIUM',
    affectedJobIds:          n.jobIds,
    affectedJobNumbers:      [],
    affectedCrewIds:         n.employeeIds,
    heading:                 `ESCALATED: ${n.heading}`,
    body:                    n.body,
    requiredAction:          n.requiredAction,
    acknowledgementRequired: true,
    linkedWHSRecordId:       notificationId,
    linkedWHSModule:         `Notification Bus (${n.type})`,
    createdById:             pmId,
    createdByName:           `${pmName} (escalated from notification)`,
  })

  return true
}

/** Delivery status summary for a single notification. */
export interface DeliveryStatusSummary {
  total:       number
  acknowledged:number
  pending:     number
  ackRate:     number      // 0–100 percentage
  acknowledgedBy: Array<{ employeeId: string; employeeName: string; acknowledgedAt: string }>
  pendingIds:  string[]
}

export async function getDeliveryStatusSummary(notificationId: string): Promise<DeliveryStatusSummary | null> {
  // Phase 6: GET /api/notifications/crew/:id/delivery
  const n = _byId(notificationId)
  if (!n) return null

  const total    = n.employeeIds.length
  const acked    = n.acknowledgements.length
  const pending  = total - acked
  const pendingIds = n.employeeIds.filter(id => !n.acknowledgements.find(a => a.employeeId === id))

  return {
    total,
    acknowledged: acked,
    pending,
    ackRate:      total > 0 ? Math.round((acked / total) * 100) : 0,
    acknowledgedBy: n.acknowledgements.map(a => ({
      employeeId:    a.employeeId,
      employeeName:  a.employeeName,
      acknowledgedAt:a.acknowledgedAt,
    })),
    pendingIds,
  }
}

// ── Date grouping ─────────────────────────────────────────────────

export type DateGroup = 'Today' | 'Yesterday' | 'This week' | 'Earlier'

export interface GroupedNotifications {
  group:  DateGroup
  date:   string
  items:  CrewNotification[]
}

export async function getNotificationsGroupedByDate(
  filter?: GetNotificationsFilter,
): Promise<GroupedNotifications[]> {
  // Phase 6: GET /api/notifications/crew?grouped=date
  const all   = _get(filter)
  const now   = new Date()
  const today = now.toDateString()
  const yesterday = new Date(now.getTime() - 86_400_000).toDateString()
  const weekAgo   = new Date(now.getTime() - 7 * 86_400_000)

  const buckets: Record<DateGroup, CrewNotification[]> = {
    'Today':     [],
    'Yesterday': [],
    'This week': [],
    'Earlier':   [],
  }

  for (const n of all) {
    const d = new Date(n.createdAt)
    if (d.toDateString() === today)        buckets['Today'].push(n)
    else if (d.toDateString() === yesterday) buckets['Yesterday'].push(n)
    else if (d >= weekAgo)                 buckets['This week'].push(n)
    else                                   buckets['Earlier'].push(n)
  }

  const ORDER: DateGroup[] = ['Today', 'Yesterday', 'This week', 'Earlier']
  return ORDER
    .filter(g => buckets[g].length > 0)
    .map(g => ({
      group: g,
      date:  g === 'Today' ? today : g === 'Yesterday' ? yesterday : g,
      items: buckets[g],
    }))
}

// ── Search ────────────────────────────────────────────────────────

export async function searchNotifications(
  query:   string,
  filter?: GetNotificationsFilter,
): Promise<CrewNotification[]> {
  // Phase 6: GET /api/notifications/crew?q=...
  if (!query.trim()) return _get(filter)

  const q   = query.toLowerCase()
  const all = _get(filter)
  return all.filter(n =>
    n.heading.toLowerCase().includes(q) ||
    n.body.toLowerCase().includes(q)    ||
    n.type.toLowerCase().replace(/_/g,' ').includes(q)
  )
}

// ── CSV export ────────────────────────────────────────────────────

export interface ExportFilter extends GetNotificationsFilter {
  dateFrom?: string
  dateTo?:   string
}

export async function exportNotificationsToCSV(filter?: ExportFilter): Promise<string> {
  // Phase 6: GET /api/notifications/crew/export?format=csv
  let all = _get(filter)

  if (filter?.dateFrom) {
    const from = new Date(filter.dateFrom)
    all = all.filter(n => new Date(n.createdAt) >= from)
  }
  if (filter?.dateTo) {
    const to = new Date(filter.dateTo)
    to.setHours(23, 59, 59)
    all = all.filter(n => new Date(n.createdAt) <= to)
  }

  const headers = [
    'ID', 'Type', 'Priority', 'Status', 'Heading',
    'Source Module', 'Job IDs', 'Recipient Count',
    'Acknowledged Count', 'Requires Ack',
    'Created At (AEST)', 'Sent At',
  ]

  function fmtAEST(iso: string): string {
    return new Date(iso).toLocaleString('en-AU', { timeZone:'Australia/Brisbane' })
  }

  function escape(s: string | number | boolean): string {
    const str = String(s)
    return str.includes(',') || str.includes('"') || str.includes('\n')
      ? `"${str.replace(/"/g, '""')}"`
      : str
  }

  const rows = all.map(n => [
    n.id,
    n.type,
    n.priority,
    n.status,
    escape(n.heading),
    n.sourceModule,
    n.jobIds.join(';'),
    n.employeeIds.length,
    n.acknowledgements.length,
    n.requiresAck ? 'Yes' : 'No',
    fmtAEST(n.createdAt),
    n.createdAt,
  ])

  return [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
}
